import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import '../components/NoLots.css';

// Component to show when user has no lots
const NoLotsMessage = () => {
  const { logout } = useUser();

  const handleLogout = async () => {
    // Clear timeframe keys before logging out (same as LotDashboard)
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith("timeframe_")) {
        localStorage.removeItem(key);
      }
    });
    await logout();
  };

  return (
    <div className="no-lots-message">
      <h1>No Lots Available</h1>
      <p>You do not have any lots assigned to your account.</p>
      <button
        onClick={handleLogout}
        className="return-button"
      >
        Return to Login
      </button>
    </div>
  );
};

const UserRedirect: React.FC = () => {
  const { fetchUserData, fetchUserLots, user, userLots, loading, error } = useUser();
  const [redirectPath, setRedirectPath] = useState<string | null>(null);
  const [noLots, setNoLots] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [hasTriedFetching, setHasTriedFetching] = useState<boolean>(false);
  const MAX_RETRIES = 2;
  const BASE_RETRY_DELAY = 1000; // 1 second base delay

  // Exponential backoff: 1s, 2s, 4s, etc.
  const getRetryDelay = (attempt: number) => BASE_RETRY_DELAY * Math.pow(2, attempt);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const loadUserData = async () => {
      // Early exit conditions to prevent infinite loops
      if (isInitialized) {
        return;
      }

      if (retryCount >= MAX_RETRIES) {
        setNoLots(true);
        setIsInitialized(true);
        return;
      }

      // If we already have user lots data (including empty array), handle it immediately
      if (hasTriedFetching && user) {
        if (userLots.length === 0) {
          const isSuperadmin = (user.role || '').toLowerCase() === 'superadmin';
          if (isSuperadmin) {
            setRedirectPath('/lot-dashboard');
            setIsInitialized(true);
            return;
          }
          setNoLots(true);
          setIsInitialized(true);
          return;
        } else if (userLots.length > 1) {
          setRedirectPath('/lot-dashboard');
          setIsInitialized(true);
          return;
        } else {
          setRedirectPath(`/lot/${userLots[0].lotId}/dashboard/revenue`);
          setIsInitialized(true);
          return;
        }
      }

      try {
        // Fetch user data if not already loaded
        if (!user) {
          await fetchUserData();
        }

        // Only fetch lots if we haven't tried before OR if we're retrying after an error
        if (!hasTriedFetching || retryCount > 0) {
          const lots = await fetchUserLots();
          setHasTriedFetching(true);

          // IMMEDIATE decision based on fetched lots
          if (lots.length === 0) {
            // Check if user is superadmin - use sessionStorage since user context may not be updated yet
            const cachedRole = sessionStorage.getItem('parallel_user_role') || '';
            const isSuperadmin = cachedRole.toLowerCase() === 'superadmin';
            if (isSuperadmin) {
              setRedirectPath('/lot-dashboard');
              setIsInitialized(true);
              return;
            }
            setNoLots(true);
            setIsInitialized(true);
            return;
          } else if (lots.length > 1) {
            setRedirectPath('/lot-dashboard');
            setIsInitialized(true);
            return;
          } else {
            setRedirectPath(`/lot/${lots[0].lotId}/dashboard/revenue`);
            setIsInitialized(true);
            return;
          }
        }

      } catch {
        // If we get an error and already tried fetching, don't retry indefinitely
        if (hasTriedFetching && retryCount >= 1) {
          setNoLots(true);
          setIsInitialized(true);
          return;
        }

        // Increment retry count and try again after exponential backoff delay
        const nextRetryCount = retryCount + 1;
        setRetryCount(nextRetryCount);

        timeoutId = setTimeout(() => {
          loadUserData();
        }, getRetryDelay(retryCount));
      }
    };

    loadUserData();

    // Cleanup timeout on unmount
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isInitialized, retryCount, user, userLots, hasTriedFetching, fetchUserData, fetchUserLots]);

  if (loading && !isInitialized) {
    return (
      <div className="loading">
        Loading user data...
      </div>
    );
  }

  if (error && isInitialized) {
    return <div className="error">Error: {error}</div>;
  }

  if (noLots) {
    return <NoLotsMessage />;
  }

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <div className="loading">
      Determining where to redirect you...
    </div>
  );
};

export default UserRedirect;